"use client";

/**
 * NinePhaseAnalysis — the 9-phase Reports framework, replayed as a loop.
 *
 * The Reports analog of Compass's `<PipelineLoop>`: where Compass shows
 * the 7-analyzer weekly cycle, this shows ONE report being produced by
 * the 9-phase fundamental framework (the real phase list from
 * `/methodology`), in order — business before financials, financials
 * before valuation, score last.
 *
 * Visual register follows the prototype's pipeline section
 * (`docs/Fable UiUx review/wtm-reports nonlogged in page -sample.html`):
 * a terminal-style card where each phase row runs WAIT → NN% → DONE with
 * believable overlap, a live phase-description line, and a footer where
 * the WTM score lands once phase 09 (Score & Evidence) completes.
 *
 * Architecture: the entire render is a PURE function of the StageFrame
 * from `useStageDriver` — no setTimeout timeline anywhere. The driver
 * loops continuously by default (no scroll/hover pause). Reduced motion
 * pins to the completed end state: all nine phases DONE, score shown.
 *
 * The score shown is illustrative of one run shape. No buy/sell/hold or
 * target-price-as-recommendation language anywhere in this component.
 */

import { useMemo } from "react";
import { useStageDriver } from "@/lib/motion";
import { cn } from "@/lib/utils/cn";
import { EASE, LINE, MONO, SERIF, clamp01, easeOutCubic, window01 } from "./shared";

// ─── Content — the real 9 phases from /methodology ──────────────────────

interface PhaseDef {
  k: string;
  name: string;
  desc: string;
}

const PHASES: PhaseDef[] = [
  {
    k: "01",
    name: "Company Snapshot",
    desc: "What the company does, who its customers are, segment revenue mix.",
  },
  {
    k: "02",
    name: "Business Model & Unit Economics",
    desc: "How it makes money at the unit level: cost structure, operating leverage.",
  },
  {
    k: "03",
    name: "Competitive Moat",
    desc: "Five moat sources rated against specific evidence.",
  },
  {
    k: "04",
    name: "Financial Analysis with Context",
    desc: "ROE, margins, growth, debt: each read in lifecycle context.",
  },
  {
    k: "05",
    name: "Growth Runway",
    desc: "Where the next decade of revenue comes from: TAM, expansion vectors.",
  },
  {
    k: "06",
    name: "Valuation",
    desc: "DCF plus relative valuation: a fair-value range, not a point estimate.",
  },
  {
    k: "07",
    name: "Risks & Red Flags",
    desc: "Regulatory, accounting, governance, concentration: what could break the thesis.",
  },
  {
    k: "08",
    name: "Investment Thesis",
    desc: "The synthesis in plain English, and what would change the answer.",
  },
  {
    k: "09",
    name: "Score & Evidence",
    desc: "Final WTM score, auditable back to the prior eight phases.",
  },
];

// ─── Timeline geometry (all in global-progress 0..1 space) ──────────────

/** All nine rows have finished by this progress; the score lands after. */
const ROWS_END = 0.86;
/** Each row's RUN window length (overlapping rows, like a real pipeline). */
const ROW_SPAN = 0.17;
/** Row i starts at i * ROW_STAGGER. */
const ROW_STAGGER = (ROWS_END - ROW_SPAN) / (PHASES.length - 1);
/** The illustrative final score (one real run shape — same register as the
 *  approved MSFT sample on the Compass surfaces). */
const FINAL_SCORE = 4.2;
/** Fictional elapsed clock the loop maps onto (a real report ≈ minutes). */
const CLOCK_TOTAL_S = 4 * 60 + 37;

export interface NinePhaseAnalysisProps {
  /** Total loop length in ms. Default ~18s. */
  cycleMs?: number;
  /** Show the small "LOOPS EVERY ~Ns · PHASE" status line. Default true. */
  showStatusLine?: boolean;
  className?: string;
}

type RowState = "wait" | "run" | "done";

function rowProgress(p: number, i: number): number {
  return window01(p, i * ROW_STAGGER, ROW_SPAN);
}

function rowState(rp: number): RowState {
  if (rp <= 0) return "wait";
  if (rp >= 1) return "done";
  return "run";
}

function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ─── Component ───────────────────────────────────────────────────────────

export function NinePhaseAnalysis({
  cycleMs = 18000,
  showStatusLine = true,
  className,
}: NinePhaseAnalysisProps) {
  const durations = useMemo(() => [Math.max(cycleMs, 1000)], [cycleMs]);
  const driver = useStageDriver<HTMLDivElement>({
    durations,
    reactStateFps: 30,
  });
  const { frame, reducedMotion } = driver;
  const p = reducedMotion ? 1 : frame.progress;

  // Per-row progress + states.
  const rows = PHASES.map((_, i) => rowProgress(p, i));
  const states = rows.map(rowState);
  const doneCount = states.filter((s) => s === "done").length;
  const complete = doneCount === PHASES.length;

  // "Current" phase = the last row that has started (for the desc line).
  let activeIdx = 0;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i] > 0) activeIdx = i;
  }
  const activePhase = PHASES[activeIdx];

  // Score lands after all nine rows complete (phase 09 IS the scoring phase).
  const scoreT = easeOutCubic(window01(p, ROWS_END + 0.02, 0.08));
  const score = FINAL_SCORE * scoreT;
  const scoreLanded = scoreT >= 1;

  const clock = formatClock(clamp01(p) * CLOCK_TOTAL_S);
  const totalSec = Math.round(durations[0] / 1000);

  return (
    <div
      ref={driver.ref}
      className={cn("text-[#F1F5F9]", className)}
      aria-label="Animated replay of one 9-phase report run: company snapshot through score and evidence, in order"
    >
      {showStatusLine && (
        <p
          className="mb-7 font-tabular text-[0.75rem] tracking-[0.08em] text-[#64748B]"
          style={{ fontFamily: MONO }}
          aria-live="off"
        >
          {reducedMotion ? (
            <>
              ONE FULL RUN · <span className="text-[#94A3B8]">COMPLETE</span>
            </>
          ) : (
            <>
              LOOPS EVERY ~{totalSec}s ·{" "}
              <span className="text-[#94A3B8]">
                PHASE {activePhase.k}: {activePhase.name.toUpperCase()}
              </span>
            </>
          )}
        </p>
      )}

      <div
        className="overflow-hidden rounded-2xl border bg-[#0A101F]"
        style={{
          borderColor: LINE,
          boxShadow: "0 24px 60px -24px rgba(0,0,0,.6)",
          fontFamily: MONO,
        }}
      >
        {/* ── Terminal bar ─────────────────────────────────────────── */}
        <div
          className="flex items-center gap-3 border-b px-5 py-3.5"
          style={{ borderColor: LINE, background: "rgba(30,41,59,.4)" }}
        >
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" aria-hidden />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" aria-hidden />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" aria-hidden />
          <span className="ml-2 truncate text-[0.68rem] tracking-[0.06em] text-[#94A3B8]">
            wtm-reports · nine-phase run
          </span>
          <span
            className="ml-auto flex items-center gap-2 text-[0.66rem] tracking-[0.1em]"
            style={{
              color: complete ? "#10B981" : "#3B82F6",
              transition: `color 400ms ${EASE}`,
            }}
          >
            <span
              className={cn(
                "h-[7px] w-[7px] rounded-full",
                !complete && !reducedMotion && "animate-pulse",
              )}
              style={{ background: complete ? "#10B981" : "#3B82F6" }}
              aria-hidden
            />
            {complete ? "COMPLETE" : "RUNNING"}
          </span>
        </div>

        {/* ── Phase rows ───────────────────────────────────────────── */}
        <div className="px-4 pb-2 pt-4 sm:px-5">
          {PHASES.map((phase, i) => {
            const rp = rows[i];
            const state = states[i];
            const pct = Math.min(99, Math.floor(rp * 100));
            const active = state === "run";
            const done = state === "done";
            return (
              <div
                key={phase.k}
                className="grid items-center gap-3 rounded-lg px-2 py-[8px] sm:gap-4"
                style={{
                  gridTemplateColumns:
                    "26px minmax(0,1fr) clamp(56px,22vw,200px) 50px",
                  background: active ? "rgba(59,130,246,.07)" : "transparent",
                  transition: `background 300ms ${EASE}`,
                }}
              >
                <span className="font-tabular text-[0.66rem] text-[#64748B]">
                  {phase.k}
                </span>
                <span
                  className="truncate text-[0.78rem]"
                  style={{
                    color: active || done ? "#F1F5F9" : "#94A3B8",
                    transition: `color 300ms ${EASE}`,
                  }}
                >
                  {phase.name}
                </span>
                <span
                  className="h-[3px] overflow-hidden rounded-full"
                  style={{ background: "rgba(148,163,184,.12)" }}
                  aria-hidden
                >
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: `${Math.round(clamp01(rp) * 100)}%`,
                      background: done ? "#10B981" : "#3B82F6",
                      transition: `background 300ms ${EASE}`,
                    }}
                  />
                </span>
                <span
                  className="text-right font-tabular text-[0.66rem]"
                  style={{
                    color: done ? "#10B981" : active ? "#3B82F6" : "#64748B",
                    transition: `color 300ms ${EASE}`,
                  }}
                >
                  {state === "wait" ? "WAIT" : done ? "DONE" : `${pct}%`}
                </span>
              </div>
            );
          })}

          {/* Live phase-description line (fixed height — no layout shift). */}
          <p
            className="mt-2 min-h-[34px] px-2 text-[0.72rem] leading-[1.5] text-[#64748B]"
            style={{ fontFamily: "var(--font-sans, inherit)" }}
            aria-live="off"
          >
            <span className="text-[#94A3B8]">
              {complete ? "Run complete." : `${activePhase.name}:`}
            </span>{" "}
            {complete
              ? "Nine phases, in order: each conclusion auditable back to the phase that produced it."
              : activePhase.desc}
          </p>
        </div>

        {/* ── Footer — counters + the score landing ────────────────── */}
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t px-5 py-3.5 text-[0.66rem] tracking-[0.05em] text-[#64748B]"
          style={{ borderColor: LINE }}
        >
          <span>
            PHASES{" "}
            <b className="font-medium font-tabular text-[#94A3B8]">
              {doneCount} / {PHASES.length}
            </b>
          </span>
          <span>
            ELAPSED{" "}
            <b className="font-medium font-tabular text-[#94A3B8]">{clock}</b>
          </span>
          <span className="ml-auto flex items-center gap-2.5">
            WTM SCORE
            <span
              className="flex items-center gap-2 rounded-md border px-2.5 py-1"
              style={{
                borderColor: scoreLanded
                  ? "rgba(245,158,11,.55)"
                  : LINE,
                color: scoreT > 0 ? "#F59E0B" : "#64748B",
                opacity: scoreT > 0 ? 1 : 0.65,
                transition: `border-color 400ms ${EASE}, color 400ms ${EASE}`,
              }}
            >
              <b
                className="font-tabular text-[0.9rem] font-medium"
                style={{ fontFamily: SERIF }}
              >
                {scoreT > 0 ? score.toFixed(1) : "-"}
              </b>
              <span className="text-[0.6rem]" style={{ color: "#64748B" }}>
                / 5
              </span>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
